import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getPricingPackagesByGameType, PricingPackage } from '../../config/pricing';
import { GameConfig } from '../../config';
import { BLITZ_GAMES, TEAM_GAMES, FAMILY_GAMES, PARTY_GAMES } from '../../config/games';
import { AppIcon, type AppIconName } from '../shared/icons';
import { useTranslation } from '../../i18n';
import {
  getGameLinkName,
  getPricingPackageDescription,
  getPricingPackageName,
  translateGames,
} from '../../i18n/helpers';

interface PremiumPageProps {
  gameType: 'blitz' | 'team' | 'family' | 'party';
}

const PremiumPage: React.FC<PremiumPageProps> = ({ gameType }) => {
  const { t, i18n } = useTranslation();
  const [packages, setPackages] = useState<PricingPackage[]>([]);
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderOffset, setSliderOffset] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const visibleTiles = 3;

  useEffect(() => {
    const gamePackages = getPricingPackagesByGameType(gameType);
    setPackages(gamePackages);
  }, [gameType]);

  useEffect(() => {
    const calculateOffset = () => {
      if (containerRef.current && sliderRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const gap = 24;
        const tileWidth = (containerWidth - (visibleTiles - 1) * gap) / visibleTiles;
        const offset = currentSlide * (tileWidth + gap);
        setSliderOffset(-offset);
      }
    };

    calculateOffset();
    window.addEventListener('resize', calculateOffset);
    return () => window.removeEventListener('resize', calculateOffset);
  }, [currentSlide, visibleTiles]);

  const handleCheckout = async (packageId: string) => {
    setLoadingPackageId(packageId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      console.log('Creating checkout session for package:', packageId);
      console.log('Backend URL:', backendUrl);

      const response = await fetch(`${backendUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packageId,
          successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing?canceled=true`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Backend error response:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Checkout session created:', data);

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error instanceof Error ? error.message : t('menu.premium.checkoutRetry');
      alert(t('menu.premium.checkoutFailed', { message: errorMessage }));
    } finally {
      setLoadingPackageId(null);
    }
  };

  const getGamesForType = (): GameConfig[] => {
    switch (gameType) {
      case 'blitz': return BLITZ_GAMES;
      case 'team': return TEAM_GAMES;
      case 'family': return FAMILY_GAMES;
      case 'party': return PARTY_GAMES;
      default: return [];
    }
  };

  const games = useMemo(() => translateGames(getGamesForType()), [gameType, i18n.language]);
  const gameTypeName = getGameLinkName(gameType);

  const allGamesBenefit = {
    icon: 'gamepad' as AppIconName,
    title: t('menu.premium.allGamesTitle', { gameType: gameTypeName }),
    description: t('menu.premium.allGamesDescription', { gameType: gameTypeName }),
    games: games,
  };

  const benefits = [
    {
      icon: 'tag' as AppIconName,
      title: t('menu.premium.benefits.customRoomNames.title'),
      description: t('menu.premium.benefits.customRoomNames.description'),
    },
    {
      icon: 'lock' as AppIconName,
      title: t('menu.premium.benefits.reservedRoom.title'),
      description: t('menu.premium.benefits.reservedRoom.description'),
    },
    {
      icon: 'barChart' as AppIconName,
      title: t('menu.premium.benefits.keepingScores.title'),
      description: t('menu.premium.benefits.keepingScores.description'),
    },
    {
      icon: 'trendingUp' as AppIconName,
      title: t('menu.premium.benefits.statistics.title'),
      description: t('menu.premium.benefits.statistics.description'),
    },
  ];

  const allBenefits = [allGamesBenefit, ...benefits];
  const totalSlides = Math.max(1, allBenefits.length - visibleTiles + 1);

  const goToSlide = (index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', color: 'var(--text-primary)' }}>
      <h2 style={{
        fontSize: '2.5rem',
        marginBottom: '1rem',
        textAlign: 'center',
        color: 'var(--color-primary)',
      }}>
        {t('menu.premium.choosePlan')}
      </h2>

      <p style={{
        textAlign: 'center',
        fontSize: '1.2rem',
        marginBottom: '3rem',
        color: 'var(--text-secondary)',
      }}>
        {t('menu.premium.choosePlanSubtitle')}
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
        marginBottom: '2rem',
      }}>
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            style={{
              backgroundColor: `rgba(var(--color-primary-rgb), 0.15)`,
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              padding: '2.5rem',
              border: `3px solid var(--color-primary)`,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '350px',
            }}
          >
            <div>
              <h4 style={{
                fontSize: '1.8rem',
                marginBottom: '1rem',
                color: 'var(--color-accent)',
              }}>
                {getPricingPackageName(pkg.id)}
              </h4>
              <p style={{
                color: 'var(--text-secondary)',
                marginBottom: '2rem',
                fontSize: '1rem',
              }}>
                {getPricingPackageDescription(pkg.id)}
              </p>
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: 'var(--color-primary)',
                marginBottom: '0.5rem',
              }}>
                {pkg.price}€
              </div>
              <div style={{
                color: 'var(--text-muted)',
                fontSize: '0.9rem',
                marginBottom: '2rem',
              }}>
                {t('menu.premium.durationAccess', { duration: pkg.duration })}
              </div>
            </div>
            <button
              onClick={() => handleCheckout(pkg.id)}
              disabled={loadingPackageId !== null}
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '10px',
                padding: '1rem 2rem',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: loadingPackageId !== null ? 'not-allowed' : 'pointer',
                opacity: loadingPackageId !== null ? 0.6 : 1,
                transition: 'all 0.3s ease',
                width: '100%',
              }}
              onMouseOver={(e) => {
                if (loadingPackageId === null) {
                  e.currentTarget.style.backgroundColor = 'var(--color-secondary)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseOut={(e) => {
                if (loadingPackageId === null) {
                  e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {loadingPackageId === pkg.id ? t('common.processing') : t('common.getStarted')}
            </button>
          </div>
        ))}
      </div>

      <h3 style={{
        fontSize: '2rem',
        textAlign: 'center',
        marginTop: '4rem',
        marginBottom: '2rem',
        color: 'var(--color-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
      }}>
        <AppIcon name="premium" sx={{ fontSize: '2rem' }} />
        {t('menu.premium.benefitsTitle')}
      </h3>

      <p style={{
        textAlign: 'center',
        fontSize: '1.2rem',
        marginBottom: '3rem',
        color: 'var(--text-secondary)',
      }}>
        {t('menu.premium.benefitsSubtitle')}
      </p>

      <div style={{ position: 'relative', marginBottom: '3rem' }}>
        <div
          ref={containerRef}
          style={{
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <div
            ref={sliderRef}
            style={{
              display: 'flex',
              gap: '1.5rem',
              transform: `translateX(${sliderOffset}px)`,
              transition: 'transform 0.3s ease',
            }}
          >
            {allBenefits.map((benefit, index) => (
              <div
                key={index}
                style={{
                  flex: `0 0 calc((100% - ${(visibleTiles - 1) * 1.5}rem) / ${visibleTiles})`,
                  minWidth: 0,
                  backgroundColor: `rgba(var(--color-primary-rgb), 0.2)`,
                  backdropFilter: 'blur(10px)',
                  borderRadius: '15px',
                  padding: '2rem',
                  border: `2px solid var(--color-primary)`,
                  textAlign: 'center',
                }}
              >
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <AppIcon name={benefit.icon} sx={{ fontSize: '3rem', color: 'var(--color-primary)' }} />
                </div>
                <h3 style={{
                  fontSize: '1.3rem',
                  marginBottom: '0.5rem',
                  color: 'var(--color-accent)',
                }}>
                  {benefit.title}
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          style={{
            position: 'absolute',
            left: '10px',
            bottom: '55px',
            backgroundColor: currentSlide === 0 ? 'rgba(255, 255, 255, 0.3)' : 'var(--color-primary)',
            color: 'var(--text-primary)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            fontSize: '1.5rem',
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            opacity: currentSlide === 0 ? 0.5 : 1,
            transition: 'all 0.3s ease',
          }}
        >
          ‹
        </button>
        <button
          onClick={nextSlide}
          disabled={currentSlide >= totalSlides - 1}
          style={{
            position: 'absolute',
            right: '10px',
            bottom: '55px',
            backgroundColor: currentSlide >= totalSlides - 1 ? 'rgba(255, 255, 255, 0.3)' : 'var(--color-primary)',
            color: 'var(--text-primary)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            fontSize: '1.5rem',
            cursor: currentSlide >= totalSlides - 1 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            opacity: currentSlide >= totalSlides - 1 ? 0.5 : 1,
            transition: 'all 0.3s ease',
          }}
        >
          ›
        </button>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '2rem',
        }}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: currentSlide === index ? '24px' : '12px',
                height: '12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: currentSlide === index ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              aria-label={t('common.goToSlide', { index: index + 1 })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PremiumPage;
